import { Button, Group, Stack, Title, Tooltip } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { openConfirmModal } from "@mantine/modals"
import { IconFilePlus, IconTrash, IconSettings } from "@tabler/icons-react"
import {
  createMRTColumnHelper,
  MantineReactTable,
  MRT_ColumnDef,
  useMantineReactTable,
} from "mantine-react-table"
import { useMemo } from "react"
import { useNavigate } from "react-router-dom"

import { DigestResponses } from "@costory/types/endpoints/digest"

import { ICON_SIZE } from "@costory/shared/const"

import { HoverHighlightCell } from "@costory/front/components/common/NavigateNewTab"
import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import { DateCell } from "@costory/front/components/table/DateCell"
import { UserCell } from "@costory/front/components/table/UserCell"
import { CreateDigestModal } from "@costory/front/pages/DigestV2/CreateModal"
import { DigestConfigStepper } from "@costory/front/pages/DigestV2/v3/DigestConfig"
import {
  useDeleteDigestMutation,
  useDigestListQuery,
} from "@costory/front/queries/digest"
import { truncateLabel } from "@costory/front/utils/charts"
import { getDefaultTableOptions } from "@costory/front/utils/table"

type Props = {
  digests: DigestResponses.ListDigests[]
}

const ListPage = ({ digests }: Props) => {
  const navigate = useNavigate()
  const mutationDelete = useDeleteDigestMutation()
  const columns = useMemo<MRT_ColumnDef<DigestResponses.ListDigests>[]>(() => {
    const columnHelper = createMRTColumnHelper<DigestResponses.ListDigests>()
    return [
      columnHelper.accessor("name", {
        header: "Name",
        Cell: ({ cell, row }) => {
          const name = cell.getValue()
          const truncated = truncateLabel(name, 25)
          return (
            <Tooltip label={name} multiline maw={350} position="top">
              <span data-intercom-target="costory-tour-digest-name-link">
                <HoverHighlightCell
                  value={truncated}
                  id={`edit/${row.original.id}`}
                  basePath="digests/"
                />
              </span>
            </Tooltip>
          )
        },
      }),
      columnHelper.accessor("period", {
        header: "Period",
      }),
      columnHelper.accessor("createdBy", {
        header: "Created By",
        Cell: ({ cell }) => <UserCell user={cell.getValue()} />,
      }),
      columnHelper.accessor("updatedAt", {
        header: "Updated At",
        Cell: ({ cell }) => <DateCell date={cell.getValue()} />,
        enableSorting: true,
      }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        enableColumnActions: false,
        Cell: ({ row }) => (
          <Group gap={8} wrap="nowrap">
            <Tooltip label="Edit">
              <Button
                size="xs"
                variant="outline"
                onClick={() => {
                  navigate(`edit/${row.original.id}`)
                }}
                data-intercom-target="costory-tour-digest-edit"
              >
                Open
              </Button>
            </Tooltip>
            <Tooltip label="View Summary">
              <Button
                size="xs"
                variant="outline"
                onClick={() => {
                  navigate(`summary/${row.original.id}`)
                }}
                data-intercom-target="costory-tour-digest-view"
              >
                Summary
              </Button>
            </Tooltip>
            <Tooltip label="Delete">
              <Button
                size="xs"
                variant="outline"
                color="red"
                leftSection={<IconTrash size={16} />}
                onClick={() =>
                  openConfirmModal({
                    title: (
                      <Title size="h3">
                        Want to archive digest {row.original.name}?
                      </Title>
                    ),
                    labels: { confirm: "Confirm", cancel: "Cancel" },
                    onConfirm: () => {
                      mutationDelete.mutateAsync(row.original.id)
                    },
                  })
                }
                data-intercom-target="costory-tour-digest-delete"
              >
                Delete
              </Button>
            </Tooltip>
          </Group>
        ),
      }),
    ]
  }, [mutationDelete, navigate])

  const table = useMantineReactTable<DigestResponses.ListDigests>({
    columns,
    data: digests,
    ...getDefaultTableOptions(),
    initialState: {
      density: "xs",
      sorting: [{ id: "updatedAt", desc: true }],
    },
    mantineTableBodyCellProps: {
      style: {
        fontSize: "var(--mantine-font-size-sm)",
      },
    },
    mantineTableHeadCellProps: {
      style: {
        paddingTop: "6px",
        paddingBottom: "6px",
        fontSize: "var(--mantine-font-size-sm)",
      },
    },
    mantineTableContainerProps: {
      style: {
        height: "90%",
      },
    },
  })
  const [openedNewModal, handlersNewModal] = useDisclosure()
  const [configModal, handlersConfigModal] = useDisclosure()
  return (
    <>
      <CreateDigestModal
        opened={openedNewModal}
        close={handlersNewModal.close}
      />
      {configModal && (
        <DigestConfigStepper
          opened={configModal}
          onClose={handlersConfigModal.close}
        />
      )}

      <Stack gap={32}>
        <Group justify="flex-end" gap="xs">
          <Button
            variant="filled"
            onClick={() => handlersConfigModal.open()}
            leftSection={<IconSettings size={ICON_SIZE} />}
            data-intercom-target="costory-tour-digest-settings"
          >
            Settings
          </Button>
          <Button
            onClick={() => handlersNewModal.open()}
            leftSection={<IconFilePlus size={ICON_SIZE} />}
            data-intercom-target="costory-tour-digest-new"
          >
            New
          </Button>
        </Group>
        <MantineReactTable table={table} />
      </Stack>
    </>
  )
}

export const ListPageWrapper = () => {
  const digestsQuery = useDigestListQuery()
  return (
    <QueryWrapper query={digestsQuery} allowEmptyArray>
      {({ data }) => <ListPage digests={data} />}
    </QueryWrapper>
  )
}
